import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { getVisualAsteroids, sortClosestFirst } from '../utils/visualAsteroids';

const WORLD_SCALE = 0.035;
const EARTH_COLOR = '#11788A';

const disposeMaterial = material => {
    if (Array.isArray(material)) {
        material.forEach(disposeMaterial);
        return;
    }

    if (material) {
        material.dispose();
    }
};

export default function AsteroidScene3D({ asteroids, outlineColor, bgColor }) {
    const containerRef = useRef(null);
    const [hoveredAsteroid, setHoveredAsteroid] = useState(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(`#${bgColor}`);

        const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 2000);
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            preserveDrawingBuffer: true,
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        container.appendChild(renderer.domElement);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.06;
        controls.enablePan = true;

        const visualAsteroids = sortClosestFirst(getVisualAsteroids(asteroids));
        const largestDiameterWorld = visualAsteroids.length
            ? Math.max(...visualAsteroids.map(a => a.visualDiameter * WORLD_SCALE))
            : 1;
        const zSpacing = Math.max(5, largestDiameterWorld * 1.35);
        const totalDepth = Math.max(0, (visualAsteroids.length - 1) * zSpacing);
        const maxGapWorld = visualAsteroids.length
            ? Math.max(...visualAsteroids.map(a => a.gapFromHorizon * WORLD_SCALE))
            : 0;
        const targetY = Math.max(4, Math.min(14, maxGapWorld / 2));

        const earthRadius = Math.max(28, totalDepth * 0.32 + 18);
        const earth = new THREE.Mesh(
            new THREE.SphereGeometry(earthRadius, 64, 32),
            new THREE.MeshStandardMaterial({
                color: EARTH_COLOR,
                roughness: 0.82,
                metalness: 0.04,
            })
        );
        earth.position.set(0, -earthRadius, 0);
        scene.add(earth);

        scene.add(new THREE.AmbientLight(0xffffff, 0.55));

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.15);
        keyLight.position.set(24, 34, 30);
        scene.add(keyLight);

        const fillLight = new THREE.PointLight(0x88ccff, 0.65, 120);
        fillLight.position.set(-18, 12, 22);
        scene.add(fillLight);

        const asteroidGeometry = new THREE.SphereGeometry(1, 32, 16);
        const outlineMaterial = outlineColor === 'white' || outlineColor === 'black'
            ? new THREE.MeshBasicMaterial({
                color: outlineColor,
                wireframe: true,
                transparent: true,
                opacity: 0.28,
            })
            : null;
        const asteroidMeshes = [];

        visualAsteroids.forEach((asteroid, index) => {
            const diameterWorld = asteroid.visualDiameter * WORLD_SCALE;
            const radiusWorld = diameterWorld / 2;
            const z = totalDepth / 2 - index * zSpacing;
            const x = ((index % 3) - 1) * Math.min(4, zSpacing * 0.35);
            const y = asteroid.gapFromHorizon * WORLD_SCALE + radiusWorld;

            const material = new THREE.MeshStandardMaterial({
                color: asteroid.color,
                roughness: 0.74,
                metalness: 0.08,
            });
            const mesh = new THREE.Mesh(asteroidGeometry, material);
            mesh.position.set(x, y, z);
            mesh.scale.setScalar(radiusWorld);
            mesh.userData.asteroid = asteroid;
            mesh.userData.rotationSpeed = 0.002 + index * 0.00018;
            scene.add(mesh);
            asteroidMeshes.push(mesh);

            if (outlineMaterial) {
                const outline = new THREE.Mesh(asteroidGeometry, outlineMaterial);
                outline.scale.setScalar(1.015);
                mesh.add(outline);
            }
        });

        const cameraDepth = Math.max(28, totalDepth * 0.55 + 28);
        camera.position.set(0, Math.max(10, maxGapWorld * 0.65 + 8), totalDepth / 2 + cameraDepth);
        camera.lookAt(0, targetY, 0);
        controls.target.set(0, targetY, 0);
        controls.minDistance = 7;
        controls.maxDistance = Math.max(80, totalDepth + 90);

        const raycaster = new THREE.Raycaster();
        const pointer = new THREE.Vector2();

        const handlePointerMove = event => {
            const rect = renderer.domElement.getBoundingClientRect();
            pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(pointer, camera);

            const [hit] = raycaster.intersectObjects(asteroidMeshes, false);
            container.style.cursor = hit ? 'pointer' : 'default';
            setHoveredAsteroid(hit ? hit.object.userData.asteroid : null);
        };

        const handlePointerLeave = () => {
            container.style.cursor = 'default';
            setHoveredAsteroid(null);
        };

        renderer.domElement.addEventListener('pointermove', handlePointerMove);
        renderer.domElement.addEventListener('pointerleave', handlePointerLeave);

        const resize = () => {
            const rect = container.getBoundingClientRect();
            const width = Math.max(1, rect.width);
            const height = Math.max(1, rect.height);
            renderer.setSize(width, height, false);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
        };

        resize();
        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(container);

        let frameId;
        const animate = () => {
            asteroidMeshes.forEach(mesh => {
                mesh.rotation.y += mesh.userData.rotationSpeed;
                mesh.rotation.x += 0.0009;
            });
            controls.update();
            renderer.render(scene, camera);
            frameId = requestAnimationFrame(animate);
        };
        animate();

        return () => {
            cancelAnimationFrame(frameId);
            resizeObserver.disconnect();
            renderer.domElement.removeEventListener('pointermove', handlePointerMove);
            renderer.domElement.removeEventListener('pointerleave', handlePointerLeave);
            controls.dispose();
            asteroidMeshes.forEach(mesh => {
                disposeMaterial(mesh.material);
            });
            asteroidGeometry.dispose();
            if (outlineMaterial) outlineMaterial.dispose();
            earth.geometry.dispose();
            disposeMaterial(earth.material);
            renderer.dispose();
            if (renderer.domElement.parentNode === container) {
                container.removeChild(renderer.domElement);
            }
        };
    }, [asteroids, bgColor, outlineColor]);

    return (
        <div className="asteroid-scene3d" ref={containerRef}>
            {hoveredAsteroid && (
                <div className="asteroid-scene3d__tooltip">
                    <strong>{hoveredAsteroid.name}</strong>
                    <span>{hoveredAsteroid.estDimAvgM.toFixed(2)} m</span>
                    <span>{hoveredAsteroid.missDistKM.toFixed(2)} km</span>
                </div>
            )}
        </div>
    );
}
